import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import type { FormProduct, ProductConfig, LocalisedProductConfig } from "@shared/types";
import {
  ProductForm,
  getProductFormBorderClass,
  getProductFormDotClass,
  type ProductFormStatus,
} from "@bcc/components/shopify-import/product-form";
import { getVariantAttributes, VariantStatusBadges } from "@bcc/components/products/product-select-form";
import { AccordionItem, AccordionTrigger } from "@bcc/components/ui/accordion";
import type { IntensityComparisonCoffee } from "@bcc/components/ui/intensity-scale";
import { Button } from "@bcc/components/ui/button";
import { cn } from "@bcc/lib/utils";
import { resolveProductImage } from "@bcc/components/shopify-import/utils/get-product-image-url";
import { getFormProductKey } from "@bcc/components/shopify-import/get-form-product-key";
import { setProductFormCollapsibleTestId, getProductFormCollapsibleBadgeTestIds } from "./configure-step.utils";

export interface ProductFormCollapsibleProps {
  product: FormProduct;
  index: number;
  productConfig: ProductConfig;
  localisedProductConfig: LocalisedProductConfig;
  isSingleBagSizeEnabled?: boolean;
  shortTermMaxWeeklyInventory?: number;
  shortTermMinWeeklyInventory?: number;
  longTermMinWeeklyInventory?: number;
  longTermMaxWeeklyInventory?: number;
  roasterNoteMaxLength?: number;
  productDescriptionMaxLength?: number;
  titleMaxLength?: number;
  regionLargeBagSize?: string[];
  marginFloorPercent?: number;
  /** The roaster's other coffees and their intensity scores, shown as
   *  comparison markers on the intensity scale. Optional — the scale renders
   *  without markers when the list is empty. */
  comparisonCoffees?: readonly IntensityComparisonCoffee[];
  titleError?: string;
  /** Whether this form's body is expanded (controlled by ConfigureStep). */
  isOpen: boolean;
  onChange: (uuid: string, product: FormProduct) => void;
  onStatusChange: (uuid: string, status: ProductFormStatus) => void;
  /** Removes this product from the batch without leaving the Configure step (§13.2). */
  onRemove: (uuid: string) => void;
}

/**
 * Wraps a single standalone ProductForm in a collapsible box whose border
 * colour reflects that form's status (batch import). Owns the status
 * locally for the border and lifts it (plus form changes) to the modal.
 */
export function ProductFormCollapsible({
  product,
  index,
  titleError,
  isOpen,
  onChange,
  onStatusChange,
  onRemove,
  isSingleBagSizeEnabled = false,
  ...formProps
}: ProductFormCollapsibleProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ProductFormStatus>("neutral");
  const uuid = getFormProductKey(product);

  const handleChange = useCallback((next: FormProduct) => onChange(uuid, next), [onChange, uuid]);
  const handleStatusChange = useCallback(
    (next: ProductFormStatus) => {
      setStatus(next);
      onStatusChange(uuid, next);
    },
    [onStatusChange, uuid]
  );
  const handleRemove = useCallback(() => onRemove(uuid), [onRemove, uuid]);

  // A duplicate-name error blocks import even when the form's own schema passes,
  // so surface it on the border/dot/label rather than showing a misleading green.
  const displayStatus: ProductFormStatus = titleError ? "error" : status;

  // Same variant-count badges as the Select-step row (§13.1), so a
  // collapsed form doesn't lose the live/pending/rejected breakdown context.
  const { variantsCountObject, hasAllVariantsImported } = useMemo(
    () => getVariantAttributes(product, isSingleBagSizeEnabled),
    [product, isSingleBagSizeEnabled]
  );
  const badgeTestIds = useMemo(() => getProductFormCollapsibleBadgeTestIds(uuid), [uuid]);

  return (
    <AccordionItem
      value={uuid}
      showLastBorder={true}
      testId={setProductFormCollapsibleTestId(uuid)}
      className={cn("rounded-lg border-2 border-b-2 bg-surface-2 px-4 transition-colors", getProductFormBorderClass(displayStatus))}
    >
      <div className="flex w-full items-start gap-5">
        {/* AccordionTrigger renders <h3 className="flex"><button className="...flex-1...">.
            The `className` prop only reaches the inner <button>, so growing the
            trigger within this row requires a wrapping element around the whole
            <AccordionTrigger> (the <h3>) rather than a className on it. */}
        <div className="min-w-0 flex-1">
          <AccordionTrigger testId={setProductFormCollapsibleTestId(`trigger-${uuid}`)}>
            <span className="flex flex-1 flex-col gap-2 text-left">
              <span className="flex items-center gap-3">
                <img
                  src={resolveProductImage(product.images, product.imageUploads)}
                  alt={product.title ?? product.id}
                  className="h-10 w-10 rounded object-cover"
                />
                <span className="font-semibold">{product.title ?? product.id}</span>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span
                    className={cn("h-2 w-2 rounded-full", getProductFormDotClass(displayStatus))}
                    data-testid={setProductFormCollapsibleTestId(`status-${uuid}`)}
                  />
                  {t(`views.shopifyImportModal.configure.formStatus.${displayStatus}`)}
                </span>
              </span>
              <VariantStatusBadges
                variantsCountObject={variantsCountObject}
                hasAllVariantsImported={hasAllVariantsImported}
                testIds={badgeTestIds}
              />
            </span>
          </AccordionTrigger>
        </div>
        {/* The row is `items-start`, so the offset that optically aligns the trash
            glyph with the accordion chevron lives on this wrapper. Putting it on
            the Button itself (as top padding) pushed the glyph outside the
            button's own 32×32 box, leaving the hit area floating above the icon. */}
        <div className="shrink-0 pt-2.5" data-testid={setProductFormCollapsibleTestId(`remove-wrapper-${uuid}`)}>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={handleRemove}
            aria-label={t("views.shopifyImportModal.configure.removeProduct")}
            testId={setProductFormCollapsibleTestId(`remove-${uuid}`)}
          >
            <Trash2 />
          </Button>
        </div>
      </div>
      {/* The form stays mounted (its own RHF instance) and is hidden via CSS
          when collapsed, so edits are preserved across collapse/expand without
          the Radix unmount that would otherwise reset the form. */}
      <div
        className={cn("pt-2 pb-4", !isOpen && "hidden")}
        data-testid={setProductFormCollapsibleTestId(`content-${uuid}`)}
        aria-hidden={!isOpen}
      >
        <ProductForm
          {...formProps}
          product={product}
          index={index}
          titleError={titleError}
          isSingleBagSizeEnabled={isSingleBagSizeEnabled}
          onChange={handleChange}
          onStatusChange={handleStatusChange}
        />
      </div>
    </AccordionItem>
  );
}
